import { useCallback, useEffect, useState } from 'react';

import { useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { notification } from '@webclient/components/notification';

import serviceBranding from '@webclient/lib/auth/serviceBranding';
import { setSession } from '@webclient/lib/jwt';

import { useEnabledAccessConfigurations } from '@webclient/hooks/useAccessConfigurations';
import useUpdateMe from '@webclient/hooks/useUpdateMe';

import { AccessConfigurationMinType } from '@webclient/shared-types/sso';

import { useTranslate } from '@tolgee/react';

const AUTH_URL = import.meta.env.VITE_KNOWZ_AUTH_URL || '';
const TOTAL_COLLAPSED_PROVIDERS = 3;

type SsoLoginProps = {
  onLogin?: () => void;
  displayAll: boolean;
};

export default function SsoLogin({ onLogin, displayAll }: SsoLoginProps) {
  const { t } = useTranslate();
  const theme = useTheme();
  const { accessConfigurationsData } = useEnabledAccessConfigurations();
  const { handleUpdateMe } = useUpdateMe();

  const [ssoWindow, setSsoWindow] = useState<Window | null>(null);

  let providers = [...(accessConfigurationsData?.data || [])];

  providers.push({
    id: -1,
    isRootDefault: true,
    isSample: false,
    provider: 'apple',
    alias: 'apple',
    name: 'Apple',
  });

  providers.sort(({ provider: providerA }, { provider: providerB }) => {
    const brandingA = serviceBranding[providerA] || serviceBranding.custom;
    const brandingB = serviceBranding[providerB] || serviceBranding.custom;

    return brandingA.order > brandingB.order ? 1 : -1;
  });

  if (!displayAll) {
    providers = providers.slice(0, TOTAL_COLLAPSED_PROVIDERS);
  }

  const onMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin === AUTH_URL) {
        const { accessJwt, accessError } = event.data;
        if (!accessJwt && !accessError) return;

        ssoWindow && ssoWindow.close();
        window.removeEventListener('message', onMessage);
        setSsoWindow(null);

        if (accessError) return notification.error({ message: accessError });

        setSession(accessJwt);
        handleUpdateMe(accessJwt);
        onLogin?.();
      }
    },
    [ssoWindow, handleUpdateMe, onLogin],
  );

  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMessage]);

  const onSsoLogin = useCallback(
    (alias: string) => {
      ssoWindow && ssoWindow.close();

      if (alias === 'apple') {
        setSsoWindow(
          window.open(
            import.meta.env.VITE_APPLE_LOGIN || '',
            'Knowz Authentication',
            'menubar=1,resizable=1,width=600,height=700',
          ),
        );

        return;
      }

      setSsoWindow(
        window.open(
          `${AUTH_URL}/connect/oauth2?alias=${alias}&process=access`,
          'Knowz Authentication',
          'menubar=1,resizable=1,width=900,height=800',
        ),
      );
    },
    [ssoWindow],
  );

  return (
    <Stack gap={4}>
      {providers.map(
        ({ provider, alias, name }: AccessConfigurationMinType, i) => {
          const branding = serviceBranding[provider] || serviceBranding.custom;

          return (
            <Button
              onClick={() => onSsoLogin(alias)}
              variant="outlined"
              color="secondary"
              key={`${name}-${i}`}
              startIcon={branding.icon(theme)}
            >
              {t(
                'layout.auth.steps.enterEmail.loginWith',
                'Continue with {name}',
                { name },
              )}
            </Button>
          );
        },
      )}
    </Stack>
  );
}
